import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Utils from '../Utils/Utils';
import { displayName } from './employeeColors';

const WEEKDAYS = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// Parse a "yyyy-MM-dd" string as a LOCAL date (no UTC shift).
export const parseYMD = (ymd) => {
    if (!ymd) return null;
    const parts = String(ymd).split('T')[0].split('-');
    if (parts.length < 3) return null;
    const [y, m, d] = parts.map((p) => parseInt(p, 10));
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return null;
    return new Date(y, m - 1, d);
};

// Local "yyyy-MM-dd" for a Date.
export const toYMD = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const weekdayOf = (ymd) => {
    const d = parseYMD(ymd);
    return d ? WEEKDAYS[d.getDay()] : '';
};

export const labelOf = (ymd) => {
    const d = parseYMD(ymd);
    return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : '';
};

export const timeOf = (signInTime) => {
    if (!signInTime) return '';
    try {
        return Utils.convertTo12HourFormat(signInTime);
    } catch (e) {
        return signInTime;
    }
};

/**
 * Download a Points Watching list as a PDF (tabular).
 * @param {object} opts
 *   title       main heading (store / module)
 *   subtitle    context line (the day, or employee + day)
 *   entries     array of point entries
 *   showGivenBy include the "Given by" column (By session lists)
 */
export const generatePointsWatchingPdf = ({ title, subtitle, entries, showGivenBy = true }) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

    doc.setFontSize(16);
    doc.setTextColor(16, 25, 53); // navy2
    doc.text(title || 'Points Watching', 40, 48);

    if (subtitle) {
        doc.setFontSize(11);
        doc.setTextColor(90, 100, 120);
        doc.text(subtitle, 40, 66);
    }

    const head = showGivenBy
        ? [['Customer', 'Points', 'Time', 'Machine', 'Source', 'Given by', 'ID Photo']]
        : [['Customer', 'Points', 'Time', 'Machine', 'Source', 'ID Photo']];

    const body = (entries || []).map((e) => {
        const flag = e.noClearSubject ? 'NO CLEAR SUBJECT' : 'OK';
        const source = String(e.source || '').toLowerCase() === 'manual' ? 'Manual' : 'By phone';
        const row = [
            e.customerName || '',
            e.points != null ? `+${e.points}` : '',
            timeOf(e.signInTime),
            e.machine || '',
            source,
        ];
        if (showGivenBy) row.push(displayName(e.employeeName));
        row.push(flag);
        return row;
    });

    autoTable(doc, {
        head,
        body,
        startY: subtitle ? 82 : 64,
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [16, 25, 53], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [238, 241, 251] },
        didParseCell: (data) => {
            // Flag column highlighted amber when "no clear subject"
            const flagCol = head[0].length - 1;
            if (data.section === 'body' && data.column.index === flagCol
                && data.cell.raw === 'NO CLEAR SUBJECT') {
                data.cell.styles.textColor = [180, 83, 9];
                data.cell.styles.fontStyle = 'bold';
            }
        },
    });

    const safe = (subtitle || title || 'points-watching')
        .replace(/[^a-z0-9]+/gi, '-')
        .toLowerCase();
    doc.save(`points-watching-${safe}.pdf`);
};
